import React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import {
  useInternalCaseThreads,
  useCreateCaseMessage,
  useUploadAttachment,
} from '../../../data/support/useSupport';
import CaseThreadSelector from './CaseThreadSelector';
import CaseThreadView from './CaseThreadView';
import CaseMessageComposer from './CaseMessageComposer';
import CreateThreadDialog from './CreateThreadDialog';

interface SupportCaseChatProps {
  caseId: string;
}

const SupportCaseChat: React.FC<SupportCaseChatProps> = ({ caseId }) => {
  const threadsQuery = useInternalCaseThreads(caseId);
  const threads = threadsQuery.data || [];
  const [selectedThreadId, setSelectedThreadId] = React.useState<string>('');
  const [newThreadOpen, setNewThreadOpen] = React.useState(false);

  // Auto-select first thread when loaded
  React.useEffect(() => {
    if (threads.length > 0 && !selectedThreadId) {
      setSelectedThreadId(threads[0].id);
    }
  }, [threads, selectedThreadId]);

  const createMessage = useCreateCaseMessage(caseId, selectedThreadId);
  const uploadAttachment = useUploadAttachment(caseId, selectedThreadId);

  const handleSend = async (body: Record<string, any>, files?: File[]) => {
    const result = await createMessage.mutateAsync({
      body,
      direction: 'outbound',
    });
    if (files?.length && result?.id) {
      await Promise.all(
        files.map((file) =>
          uploadAttachment.mutateAsync({ messageId: result.id, file }),
        ),
      );
    }
  };

  if (threadsQuery.isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 4,
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!threads.length) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          No threads yet
        </Typography>
        <CaseThreadSelector
          threads={[]}
          selected=""
          onSelect={() => {}}
          onNewThread={() => setNewThreadOpen(true)}
        />
        <CreateThreadDialog
          open={newThreadOpen}
          onClose={() => setNewThreadOpen(false)}
          caseId={caseId}
          onCreated={(id) => setSelectedThreadId(id)}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <CaseThreadSelector
        threads={threads}
        selected={selectedThreadId}
        onSelect={setSelectedThreadId}
        onNewThread={() => setNewThreadOpen(true)}
      />

      {selectedThreadId && (
        <>
          <Box
            sx={{
              flex: 1,
              minHeight: 300,
              maxHeight: 500,
              overflowY: 'auto',
            }}
          >
            <CaseThreadView caseId={caseId} threadId={selectedThreadId} />
          </Box>

          <Box sx={{ mt: 2 }}>
            <CaseMessageComposer
              onSend={handleSend}
              isPending={createMessage.isPending || uploadAttachment.isPending}
              placeholder="Type a reply..."
            />
          </Box>
        </>
      )}

      <CreateThreadDialog
        open={newThreadOpen}
        onClose={() => setNewThreadOpen(false)}
        caseId={caseId}
        onCreated={(id) => setSelectedThreadId(id)}
      />
    </Box>
  );
};

export default SupportCaseChat;
